// src/pages/EnhancedAthletes.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getAllPlayers, getFlagUrl } from '../services/athleteDataService';
import AthleteCard from '../components/AthleteCard';

const EnhancedAthletes = () => {
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPosition, setSelectedPosition] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [featuredAthlete, setFeaturedAthlete] = useState(null);
  const [featuredAthletes, setFeaturedAthletes] = useState([]);
  const scrollRef = useRef(null);
  
  // Load athlete data from centralized service
  useEffect(() => {
    try {
      const allAthletes = getAllPlayers();
      setAthletes(allAthletes);
      
      // Set a featured athlete (randomly or the first one)
      if (allAthletes.length > 0) {
        const randomIndex = Math.floor(Math.random() * Math.min(5, allAthletes.length));
        setFeaturedAthlete(allAthletes[randomIndex]);
        
        // Get 3 featured athletes for the showcase section
        const featured = allAthletes.filter(a => a.featured).slice(0, 3);
        setFeaturedAthletes(featured.length >= 3 ? featured : allAthletes.slice(0, 3));
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading athletes:', error);
      setLoading(false);
    }
  }, []);

  // Filter athletes based on position and search term
  const filteredAthletes = athletes.filter(athlete => {
    const matchesPosition = selectedPosition === 'all' || 
                           (athlete.position && athlete.position.toLowerCase().includes(selectedPosition)) ||
                           (athlete.category && athlete.category === selectedPosition);
    const matchesSearch = !searchTerm || 
                          athlete.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                          (athlete.team && athlete.team.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesPosition && matchesSearch;
  });

  // Function to scroll to grid when clicking on discover button
  const scrollToGrid = () => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Position filter options
  const positions = [
    { id: 'all', name: 'Todos' },
    { id: 'portero', name: 'Porteros' },
    { id: 'defensa', name: 'Defensas' },
    { id: 'mediocampista', name: 'Mediocampistas' },
    { id: 'delantero', name: 'Delanteros' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-white">
        <div className="relative">
          <div className="w-24 h-24 border-t-4 border-r-4 border-red-600 rounded-full animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-red-600 font-bold">AFY</span>
          </div>
        </div>
        <p className="mt-6 text-xl font-medium bg-gradient-to-r from-white to-gray-400 bg-clip-text text-transparent">Cargando talento estelar...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section with Parallax Effect */}
      <section className="relative h-[85vh] flex items-center justify-center overflow-hidden">
        {/* Hero Background with Overlay */}
        <div className="absolute inset-0 bg-black">
          {/* Background gradient and overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/90 to-black z-10"></div>
          
          {/* Dynamic background elements */}
          <div className="absolute inset-0 z-0 overflow-hidden">
            <div className="absolute left-0 right-0 top-1/3 h-px bg-red-600/70 animate-growWidth"></div>
            <div className="absolute left-0 right-0 bottom-1/3 h-px bg-red-600/40 animate-growWidthDelayed"></div>
            <div className="absolute top-[45%] left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-radial-gradient opacity-20" 
              style={{ background: 'radial-gradient(circle at center, rgba(220, 38, 38, 0.3) 0%, rgba(0, 0, 0, 0) 70%)' }}></div>
          </div>
        </div>
        
        {/* Hero Content */}
        <div className="relative z-20 text-center px-4 max-w-6xl mx-auto">
          <h1 className="text-6xl md:text-8xl font-black mb-6 animate-fadeInUp opacity-0 tracking-tight" 
              style={{ 
                animationDelay: '0.2s', 
                animationFillMode: 'forwards',
                textShadow: '0 0 30px rgba(255, 255, 255, 0.2)'
              }}>
            ESTRELLAS <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-600 to-red-400">AFY</span>
          </h1>
          
          <div className="h-1.5 w-32 bg-gradient-to-r from-red-600 to-red-400 mx-auto mb-10 animate-growWidth rounded-full shadow-lg shadow-red-600/20" 
               style={{ animationDelay: '0.6s', animationDuration: '1.5s' }}></div>
          
          <p className="max-w-3xl mx-auto text-lg md:text-2xl text-gray-300 mb-12 animate-fadeInUp opacity-0 leading-relaxed" 
             style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
            Descubre nuestro <span className="text-white font-semibold">exclusivo roster</span> de talentos futbolísticos representados por 
            <span className="text-red-500 font-bold"> AFY GROUP</span>, estrellas de élite disponibles para clubes de todo el mundo.
          </p>
          
          <button 
            onClick={scrollToGrid}
            className="group relative overflow-hidden px-10 py-5 bg-gradient-to-r from-red-700 to-red-500 rounded-full text-white font-bold transition-all duration-500 animate-fadeInUp opacity-0 shadow-xl hover:shadow-red-600/30"
            style={{ animationDelay: '1s', animationFillMode: 'forwards' }}
          >
            <span className="relative z-10 text-lg tracking-wider uppercase">DESCUBRIR ATLETAS</span>
            <span className="absolute inset-0 w-0 bg-gradient-to-r from-red-600 to-red-400 transition-all duration-500 group-hover:w-full"></span>
          </button>
        </div>
        
        {/* Scrolling indicator with animation */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce z-20">
          <div className="flex flex-col items-center">
            <span className="text-red-500 text-xs uppercase tracking-widest mb-2 font-medium">Scroll</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
              <path d="M12 5v14M5 12l7 7 7-7"/>
            </svg>
          </div>
        </div>
      </section>

      {/* Featured Athletes Showcase - Horizontal Scrolling */}
      {featuredAthletes.length > 0 && (
        <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900 overflow-hidden">
          <div className="container mx-auto">
            <div className="flex items-center mb-12">
              <div className="w-12 h-0.5 bg-red-600 mr-6"></div>
              <h2 className="text-3xl md:text-4xl font-black relative">
                Atletas Destacados
                <span className="absolute -bottom-2 left-0 w-1/2 h-1 bg-red-600"></span>
              </h2>
            </div>
            
            {/* Featured Athletes Slider */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {featuredAthletes.map((athlete, index) => (
                <div 
                  key={athlete.id} 
                  className="transform transition-all duration-700 opacity-0 translate-y-12"
                  style={{ 
                    animationName: 'fadeInUp',
                    animationDuration: '1s',
                    animationDelay: `${0.2 * index}s`,
                    animationFillMode: 'forwards'
                  }}
                >
                  <AthleteCard athlete={athlete} variant="premium" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filter Section - Elegant & Professional */}
      <section ref={scrollRef} className="py-16 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="container mx-auto">
          <div className="bg-gradient-to-br from-black to-gray-900 rounded-xl p-8 shadow-2xl border border-gray-800 relative overflow-hidden">
            {/* Background accent */}
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-red-600 to-transparent"></div>
            <div className="absolute bottom-0 right-0 w-full h-1 bg-gradient-to-l from-red-600 to-transparent"></div>
            
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              {/* Position Filter - Elevated Design */}
              <div>
                <h3 className="text-sm uppercase tracking-wider text-gray-400 mb-3 flex items-center">
                  <div className="w-4 h-0.5 bg-red-600 mr-2"></div>
                  Filtrar por posición
                </h3>
                <div className="flex flex-wrap gap-2">
                  {positions.map(position => (
                    <button
                      key={position.id}
                      onClick={() => setSelectedPosition(position.id)}
                      className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                        selectedPosition === position.id
                          ? 'bg-gradient-to-r from-red-700 to-red-500 text-white shadow-lg shadow-red-600/20 border border-red-500/30'
                          : 'bg-gray-800/80 text-gray-300 hover:bg-gray-700 border border-gray-700'
                      }`}
                    >
                      {position.name}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Search Box - Elevated Design */}
              <div className="relative w-full lg:w-auto">
                <h3 className="text-sm uppercase tracking-wider text-gray-400 mb-3 flex items-center">
                  <div className="w-4 h-0.5 bg-red-600 mr-2"></div>
                  Buscar atleta
                </h3>
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Buscar por nombre o equipo..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full lg:w-80 px-5 py-3.5 pl-12 bg-gray-800/70 text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-red-600 transition-all duration-300 border border-gray-700 shadow-inner"
                  />
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 absolute left-4 top-4 text-gray-400">
                    <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                  </svg>
                </div>
              </div>
            </div>
            
            {/* Results count - Elegant styling */}
            <div className="mt-8 flex items-center justify-between border-t border-gray-800 pt-6">
              <span className="text-sm text-gray-400 flex items-center">
                <div className="w-2 h-2 rounded-full bg-red-600 mr-2"></div>
                <strong className="font-medium text-white">{filteredAthletes.length}</strong> {filteredAthletes.length === 1 ? 'atleta encontrado' : 'atletas encontrados'}
              </span>
              
              {(selectedPosition !== 'all' || searchTerm) && (
                <button 
                  onClick={() => {
                    setSelectedPosition('all');
                    setSearchTerm('');
                  }}
                  className="text-sm text-red-400 hover:text-red-300 transition-colors flex items-center group"
                >
                  <span>Limpiar filtros</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-1 transition-transform duration-300 group-hover:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Athletes Grid - Using Enhanced AthleteCards */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900/80">
        <div className="container mx-auto">
          {filteredAthletes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 lg:gap-10">
              {filteredAthletes.map((athlete, index) => (
                <div
                  key={athlete.id}
                  className="opacity-0 animate-fadeInUp"
                  style={{ 
                    animationDelay: `${0.05 * index}s`,
                    animationFillMode: 'forwards'
                  }}
                >
                  <AthleteCard athlete={athlete} variant="default" className="h-full" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-32 bg-gradient-to-b from-gray-900/20 to-black rounded-xl border border-gray-800/50 shadow-2xl">
              <div className="relative w-24 h-24 mx-auto mb-6">
                <div className="absolute inset-0 bg-red-600/10 rounded-full animate-ping"></div>
                <div className="relative flex items-center justify-center w-24 h-24 bg-gray-900/80 rounded-full border border-red-600/20">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-gray-400 mb-3">No se encontraron atletas</h3>
              <p className="text-gray-500 max-w-md mx-auto mb-8">La búsqueda actual no coincide con ningún atleta en nuestra base de datos</p>
              <button 
                onClick={() => {
                  setSelectedPosition('all');
                  setSearchTerm('');
                }}
                className="px-8 py-3 bg-gradient-to-r from-red-700 to-red-500 rounded-full text-white font-bold hover:shadow-lg hover:shadow-red-600/20 transition-all duration-300 transform hover:-translate-y-1"
              >
                Reiniciar búsqueda
              </button>
            </div>
          )}
        </div>
      </section>
      
      {/* Call to Action - Elite & Professional */}
      <section className="relative py-32 px-4 overflow-hidden">
        {/* Background with dramatic lighting */}
        <div className="absolute inset-0 bg-black">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-red-900/20 via-black to-black"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-radial-gradient opacity-20"
               style={{ background: 'radial-gradient(circle at center, rgba(220, 38, 38, 0.3) 0%, rgba(0, 0, 0, 0) 70%)' }}></div>
        </div>
        
        <div className="container mx-auto relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="inline-block text-4xl md:text-5xl font-black mb-8 relative">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-300">¿Buscas representación deportiva?</span>
              <div className="absolute -bottom-4 left-1/2 transform -translate-x-1/2 w-24 h-1 bg-gradient-to-r from-red-600 to-red-400 rounded-full"></div>
            </h2>
            
            <p className="text-xl text-gray-300 mb-12 leading-relaxed">
              AFY GROUP ofrece representación profesional de élite para futbolistas de todos los niveles.
              Contacta con nosotros para conocer cómo podemos potenciar tu carrera deportiva.
            </p>
            
            <a 
              href="/contacto" 
              className="group relative inline-block"
            >
              <div className="absolute -inset-1 bg-gradient-to-r from-red-600 to-red-400 rounded-full blur-md opacity-70 group-hover:opacity-100 transition-opacity duration-300 animate-pulse"></div>
              <div className="relative px-10 py-5 bg-gradient-to-r from-red-700 to-red-500 rounded-full text-white font-bold overflow-hidden shadow-xl">
                {/* Button background animation */}
                <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-red-600 to-red-400 transition-transform duration-500 transform translate-x-full group-hover:translate-x-0"></div>
                
                {/* Button content */}
                <div className="relative z-10 flex items-center justify-center space-x-2">
                  <span className="uppercase tracking-wider text-lg">CONTACTAR AHORA</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 transform group-hover:translate-x-1 transition-transform duration-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>
      
      {/* Additional CSS animations */}
      <style jsx>{`
        @keyframes growWidth {
          from { width: 0; }
          to { width: 100%; }
        }

        @keyframes growWidthDelayed {
          0% { width: 0; }
          30% { width: 0; }
          100% { width: 100%; }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .animate-growWidth {
          animation: growWidth 2s forwards;
        }

        .animate-growWidthDelayed {
          animation: growWidthDelayed 2.5s forwards;
        }

        .animate-fadeInUp {
          animation: fadeInUp 1s forwards;
        }
        
        .animate-fadeInRight {
          animation: fadeInRight 1s forwards;
        }
        
        .animate-ping-slow {
          animation: ping 3s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        
        @keyframes ping {
          0% {
            transform: scale(1);
            opacity: 0.8;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.4;
          }
          100% {
            transform: scale(1);
            opacity: 0.8;
          }
        }
      `}</style>
    </div>
  );
};

export default EnhancedAthletes;